import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react"
import { LayerGroup, LayersControl, Polyline } from "react-leaflet"
import { useTranslation } from "react-i18next"
import { useTheme } from "@mui/material"
import type { Polyline as LeafletPolyline } from "leaflet"

import type {
  DeviceFeaturePropsWGroups,
  GeoJSONLayersData,
} from "types/geolocation.types"
import type { LastLimiterBySpanId, PeakSpanMarker } from "types/spans.types"

import {
  checkEmptyGeoData,
  formatMultiStringCoordinates,
  formatPointCollectionCoordinates,
  formatPointCoordinates,
  getMarkerIcon,
  getScaledIcon,
  TOWER,
} from "helpers/utils/map"
import { hasDeviceTriggeredAlerts } from "helpers/utils/alerts"
import type { AlertSettingStatesById } from "types/alerts.types"
import useDevices from "helpers/hooks/useDevices"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import MapMarker from "widgets/plots/geo/MapMarker"
import GeoPopup from "widgets/plots/geo/GeoPopup"
import { getDeviceVersionAndTarget, isVirtualDevice } from "helpers/utils/devices"
import { nameOrUnnamed } from "helpers/utils/translations"
import {
  POLYLINE_DEFAULT_OPACITY,
  POLYLINE_DEFAULT_WEIGHT,
  POLYLINE_HOVERED_WEIGHT,
  POLYLINE_NOT_HOVERED_OPACITY,
  TOOLTIP_THRESHOLD,
} from "helpers/utils/constants"
import SpanLayer from "widgets/plots/geo/SpanLayer"
import { useZoom } from "./ZoomContext"
import { useOverlaysChecked } from "./useOverlaysChecked"

type OverlayKey = "Powerline" | "PowerlineSpan" | "PowerlineTower" | "Device"

const OVERLAY_KEYS: readonly OverlayKey[] = [
  "Powerline",
  "PowerlineSpan",
  "PowerlineTower",
  "Device",
]

type LayersVisibility = Partial<Record<OverlayKey, boolean>>

/**
 * Everything the span layer needs, which is all of it or none of it: a map that
 * only locates a powerline passes no `spans` at all, and the one card that
 * draws the ampacity layer drives the whole group from its own state.
 */
export interface SpansOptions {
  lastLimiterBySpanId?: LastLimiterBySpanId
  peakSpanMarkers?: PeakSpanMarker[]
  // Draw every span as a plain route and highlight only the spans that earn
  // it: the currently-limiting ones (alert icon) and `peakSpanMarkers` (rings).
  neutralRoute?: boolean
  // Controlled hover. Falls back to local state when omitted.
  hoveredSpanId?: number | null
  // Opens that span's popup, so a ribbon click can drive the map, and keeps
  // the segment lit while the rest of the route dims.
  selectedSpanId?: number | null
  // Emits hover so a parent can sync another view, e.g. the ribbon.
  onHoverSpan?: (spanId: number | null) => void
  onSelectSpan?: (spanId: number | null) => void
  // Emits the span whose popup leaflet opened, or null when it closed.
  onSelectedSpanChange?: (spanId: number | null) => void
  // Set to the parent's fly-to duration so the popup lands after the pan.
  popupOpenDelay?: number
  // Whether a selection from outside also opens the popup. Off on a tight map,
  // where it would cover what you just flew to. Tapping a segment still opens one.
  autoOpenSpanPopups?: boolean
}

interface GeoJSONLayersProps {
  data: GeoJSONLayersData | undefined
  layersVisibility?: LayersVisibility
  collapsed?: boolean
  showLayersControl?: boolean
  // Smaller device icons, no pinned name. For a map with no room to spare.
  compactDevices?: boolean
  // Off on a map that only shows where things are. Nothing on it opens a popup.
  showPopups?: boolean
  spans?: SpansOptions
}

const GeoJSONLayers: FC<GeoJSONLayersProps> = ({
  data,
  layersVisibility,
  collapsed = true,
  showLayersControl = true,
  compactDevices = false,
  showPopups = true,
  spans,
}) => {
  const {
    lastLimiterBySpanId,
    peakSpanMarkers,
    neutralRoute = false,
    hoveredSpanId: controlledHoveredSpanId,
    selectedSpanId = null,
    onHoverSpan,
    onSelectSpan,
    onSelectedSpanChange,
    popupOpenDelay = 0,
    autoOpenSpanPopups = true,
  } = spans ?? {}

  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const zoom = useZoom()

  const { orgAlertsBySettingId } = useAlertSettings()
  const { devsWRelsByIdWStates } = useDevices()

  const [hoveredPowerlineId, setHoveredPowerlineId] = useState<number | null>(null)
  const [localHoveredSpanId, setLocalHoveredSpanId] = useState<number | null>(null)
  const spanLayerRefs = useRef(new Map<number, LeafletPolyline>())
  // The hit-area polylines own the popups, so an outside open goes through these.
  const spanHitLayerRefs = useRef(new Map<number, LeafletPolyline>())

  // Controlled when a parent syncs map and ribbon, local otherwise.
  const isControlledHover = onHoverSpan !== undefined
  const hoveredSpanId = isControlledHover
    ? (controlledHoveredSpanId ?? null)
    : localHoveredSpanId

  const peakMarkersBySpanId = useMemo(() => {
    const map = new Map<number, PeakSpanMarker[]>()
    for (const marker of peakSpanMarkers ?? []) {
      const list = map.get(marker.spanId) ?? []
      list.push(marker)
      map.set(marker.spanId, list)
    }
    return map
  }, [peakSpanMarkers])

  // Each line's spans in order, so an unnamed span can go by its position.
  const spanIdsByPowerlineId = useMemo(
    () =>
      Object.fromEntries(
        (data?.Powerline?.features ?? []).map(({ properties }) => [
          properties.id,
          properties.span_ids,
        ]),
      ) as Record<number, number[]>,
    [data],
  )

  const handlePowerlineMouseOver = useCallback((id: number) => {
    setHoveredPowerlineId(id)
  }, [])

  const handlePowerlineMouseOut = useCallback(() => {
    setHoveredPowerlineId(null)
  }, [])

  const handleSpanMouseOver = useCallback(
    (id: number) => {
      if (isControlledHover) onHoverSpan?.(id)
      else setLocalHoveredSpanId(id)
    },
    [isControlledHover, onHoverSpan],
  )

  const handleSpanMouseOut = useCallback(() => {
    if (isControlledHover) onHoverSpan?.(null)
    else setLocalHoveredSpanId(null)
  }, [isControlledHover, onHoverSpan])

  // Lift the hovered span so a neighbour can't cover its highlight. Needed when
  // the hover came from the ribbon: no leaflet mouse event reaches the segment.
  useEffect(() => {
    if (hoveredSpanId == null) return
    spanLayerRefs.current.get(hoveredSpanId)?.bringToFront()
  }, [hoveredSpanId])

  // A span selected from the ribbon: lift it, then open its popup after the pan.
  useEffect(() => {
    if (selectedSpanId == null) return
    // Lifting always matters: it's how you tell which span the map flew to.
    spanLayerRefs.current.get(selectedSpanId)?.bringToFront()
    if (!autoOpenSpanPopups) return
    const openPopup = () => spanHitLayerRefs.current.get(selectedSpanId)?.openPopup()
    if (!popupOpenDelay) {
      openPopup()
      return
    }
    const timer = setTimeout(openPopup, popupOpenDelay)
    return () => clearTimeout(timer)
  }, [selectedSpanId, popupOpenDelay, autoOpenSpanPopups])

  const _layersVisibility = useMemo(
    () => ({
      Powerline: true,
      PowerlineSpan: false,
      PowerlineTower: false,
      Device: true,
      ...layersVisibility,
    }),
    [layersVisibility],
  )

  const layerNames = {
    Powerline: t("geo_info.POWERLINES"),
    PowerlineSpan: t("geo_info.SPANS"),
    PowerlineTower: t("geo_info.TOWERS"),
    Device: t("geo_info.DEVICES"),
  }

  // An overlay that is turned off still renders its children, so its markers and
  // polylines are built for nothing. Keep the group empty until it is on the map.
  const { checked, setOverlayRef } = useOverlaysChecked(OVERLAY_KEYS, _layersVisibility)

  const isEmpty = useMemo(() => checkEmptyGeoData(data), [data])

  const renderPowerlineLayer = () => {
    if (!data?.Powerline || data.Powerline.features.length === 0) return null
    if (!showLayersControl && !_layersVisibility.Powerline) return null

    return (
      <LayerGroup ref={setOverlayRef.Powerline}>
        {checked.Powerline &&
          data.Powerline.features.map((feature) => {
            const coordinates = formatMultiStringCoordinates(feature.geometry.coordinates)
            const isHovered = hoveredPowerlineId === feature.properties.id
            const isOtherHovered = hoveredPowerlineId !== null && !isHovered
            return (
              <Polyline
                key={feature.properties.id}
                interactive={showPopups}
                pathOptions={{
                  color: theme.palette.primary.main,
                  weight: isHovered ? POLYLINE_HOVERED_WEIGHT : POLYLINE_DEFAULT_WEIGHT,
                  opacity: isOtherHovered
                    ? POLYLINE_NOT_HOVERED_OPACITY
                    : POLYLINE_DEFAULT_OPACITY,
                }}
                positions={coordinates}
                eventHandlers={{
                  mouseover: () => handlePowerlineMouseOver(feature.properties.id),
                  mouseout: () => handlePowerlineMouseOut(),
                }}
              >
                {showPopups && <GeoPopup type="powerline" data={feature.properties} />}
              </Polyline>
            )
          })}
      </LayerGroup>
    )
  }

  const renderSpansLayer = () => {
    if (!data?.PowerlineSpan || data.PowerlineSpan.features.length === 0) return null
    if (!showLayersControl && !_layersVisibility.PowerlineSpan) return null

    return (
      <LayerGroup ref={setOverlayRef.PowerlineSpan}>
        {checked.PowerlineSpan &&
          data.PowerlineSpan.features.map((feature) => {
            const spanId = feature.properties.id
            // An open popup reads as hover, and keeps the rest of the route dim.
            const isSelected = selectedSpanId === spanId
            const isHovered = hoveredSpanId === spanId || isSelected

            return (
              <SpanLayer
                key={spanId}
                feature={feature}
                isHovered={isHovered}
                isDimmed={
                  (hoveredSpanId !== null || selectedSpanId !== null) && !isHovered
                }
                neutralRoute={neutralRoute}
                limitingTelemetry={lastLimiterBySpanId?.[spanId]}
                peakMarkers={peakMarkersBySpanId.get(spanId)}
                showPopups={showPopups}
                lineSpanIds={spanIdsByPowerlineId[feature.properties.powerline_id]}
                spanLayerRefs={spanLayerRefs}
                spanHitLayerRefs={spanHitLayerRefs}
                onHoverSpan={handleSpanMouseOver}
                onHoverOut={handleSpanMouseOut}
                onSelectSpan={onSelectSpan}
                onSelectedSpanChange={onSelectedSpanChange}
              />
            )
          })}
      </LayerGroup>
    )
  }

  const renderTowersLayer = () => {
    if (!data?.PowerlineTower || data.PowerlineTower.features.length === 0) return null
    if (!showLayersControl && !_layersVisibility.PowerlineTower) return null

    return (
      <LayerGroup ref={setOverlayRef.PowerlineTower}>
        {checked.PowerlineTower &&
          data.PowerlineTower.features.map((feature) => {
            const position =
              "geometries" in feature.geometry
                ? formatPointCollectionCoordinates(feature.geometry)
                : formatPointCoordinates(feature.geometry)
            return (
              <MapMarker
                key={feature.id}
                popup={
                  showPopups ? (
                    <GeoPopup type="tower" data={feature.properties} />
                  ) : undefined
                }
                position={position}
                icon={TOWER}
                zIndexOffset={0}
                tooltipContent={nameOrUnnamed(feature.properties.name, t)}
              />
            )
          })}
      </LayerGroup>
    )
  }

  const renderDevicesLayer = () => {
    if (!data?.Device || data.Device.features.length === 0) return null
    if (!showLayersControl && !_layersVisibility.Device) return null

    return (
      <LayerGroup ref={setOverlayRef.Device}>
        {checked.Device &&
          data.Device.features.map((feature) => {
            const { less_id: lessId } = feature.properties
            const deviceLocation = {
              device: { ...feature.properties },
              groups: devsWRelsByIdWStates[feature.properties?.less_id]?.groups ?? [],
            } as DeviceFeaturePropsWGroups
            const triggered = Boolean(
              devsWRelsByIdWStates[lessId]?.settingsStates &&
                hasDeviceTriggeredAlerts(
                  devsWRelsByIdWStates[lessId].settingsStates as AlertSettingStatesById,
                  orgAlertsBySettingId,
                ),
            )
            const { target, version } = getDeviceVersionAndTarget(deviceLocation.device)
            const baseIcon = getMarkerIcon(triggered, version, target)
            const icon = compactDevices ? getScaledIcon(baseIcon, 0.65) : baseIcon

            if (feature.properties && isVirtualDevice(feature.properties.device_type))
              return

            return (
              <MapMarker
                key={lessId}
                popup={
                  showPopups ? (
                    <GeoPopup type="device" data={deviceLocation} />
                  ) : undefined
                }
                position={formatPointCoordinates(feature.geometry)}
                zIndexOffset={triggered ? 500 : 100}
                icon={icon}
                tooltipContent={deviceLocation.device.name}
                tooltipProps={{
                  permanent: !compactDevices && zoom >= TOOLTIP_THRESHOLD,
                }}
              />
            )
          })}
      </LayerGroup>
    )
  }

  if (isEmpty || !data) return null

  if (showLayersControl) {
    return (
      // react-leaflet registers an overlay name once, so the control keeps the
      // labels of the language it was mounted with. Remount it on a switch.
      <LayersControl
        key={i18n.resolvedLanguage ?? "en"}
        position="topright"
        collapsed={collapsed}
      >
        {data.Powerline && data.Powerline.features.length > 0 && (
          <LayersControl.Overlay checked={checked.Powerline} name={layerNames.Powerline}>
            {renderPowerlineLayer()}
          </LayersControl.Overlay>
        )}
        {data.PowerlineSpan && data.PowerlineSpan.features.length > 0 && (
          <LayersControl.Overlay
            checked={checked.PowerlineSpan}
            name={layerNames.PowerlineSpan}
          >
            {renderSpansLayer()}
          </LayersControl.Overlay>
        )}
        {data.PowerlineTower && data.PowerlineTower.features.length > 0 && (
          <LayersControl.Overlay
            checked={checked.PowerlineTower}
            name={layerNames.PowerlineTower}
          >
            {renderTowersLayer()}
          </LayersControl.Overlay>
        )}
        {data.Device && data.Device.features.length > 0 && (
          <LayersControl.Overlay checked={checked.Device} name={layerNames.Device}>
            {renderDevicesLayer()}
          </LayersControl.Overlay>
        )}
      </LayersControl>
    )
  }

  return (
    <>
      {renderPowerlineLayer()}
      {renderSpansLayer()}
      {renderTowersLayer()}
      {renderDevicesLayer()}
    </>
  )
}

export default GeoJSONLayers
